import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaBell, FaUserEdit, FaTimes, FaExclamationTriangle, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { getList, UpdateRecord, SuccessAlert, ErrorAlert, URLPRIFIX } from "../../functions/api";
import { getlocal } from "../../functions/localstorage";
import { useStore } from "../../Components/StateProvider";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

// ─── helpers ─────────────────────────────────────────────────────────────────
const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const priorityClass = (p) => {
  switch ((p || "").toUpperCase()) {
    case "HIGH": return "bg-red-50 text-red-700 border border-red-200";
    case "MEDIUM": return "bg-amber-50 text-amber-700 border border-amber-200";
    case "LOW": return "bg-green-50 text-green-700 border border-green-200";
    default: return "bg-gray-50 text-gray-600 border border-gray-200";
  }
};

const MODULE_TYPE = 11; // Non-Litigation

// ─── component ───────────────────────────────────────────────────────────────
const OverdueTasks = () => {
  const navigate = useNavigate();
  const store = useStore();
  const user = getlocal("system")?.user || {};

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  // dropdown option lists (derived from data)
  const [projectOpts, setProjectOpts] = useState([]);
  const [userOpts, setUserOpts] = useState([]);

  // filter state
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("All Projects");
  const [userFilter, setUserFilter] = useState("All Users");
  const [priorityFilter, setPriorityFilter] = useState("All Priorities");
  const [daysFilter, setDaysFilter] = useState("All");

  // sort state
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // users list for reassign (from /users/getusers)
  const [allUsers, setAllUsers] = useState([]);

  // notify modal
  const [notifyModal, setNotifyModal] = useState(null); // task obj
  const [notifying, setNotifying] = useState(false);

  // reassign modal
  const [reassignModal, setReassignModal] = useState(null); // task obj
  const [selectedUser, setSelectedUser] = useState("");
  const [reassigning, setReassigning] = useState(false);

  // message content for notification reminder
  const [reminderMessage, setReminderMessage] = useState("");

  // ── fetch overdue tasks ──────────────────────────────────────────────────
  const fetchOverdueTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getList({
        listUrl: `/subtask/getoverduetasks/${MODULE_TYPE}`,
        field: "",
        search: "",
        sort: "asc",
        page: 1,
        count: 500,
      });
      let list = res?.data?.List || res?.data?.list || [];
      
      // Filter list for non-admin users
      if (Number(user.role) !== 1) {
        list = list.filter(t => Number(t.assigned_to) === Number(user.id));
      }
      
      setAllData(list);

      // build unique dropdown options
      const projects = [...new Set(list.map(t => t.project_name).filter(Boolean))].sort();
      const users = [...new Set(list.map(t => t.assigned_to_name || t.assigned_to_username).filter(Boolean))].sort();
      setProjectOpts(projects);
      setUserOpts(users);
    } catch (e) {
      ErrorAlert("Failed to load overdue tasks.");
    } finally {
      setLoading(false);
    }
  }, [user.role, user.id]);

  // ── fetch users list for reassign ────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    try {
      const res = await getList({
        listUrl: "/users/getusers",
        field: "userName",
        sort: "asc",
        page: 1,
        count: 500,
      });
      const list = res?.data?.list || [];
      setAllUsers(list.map(u => ({
        value: u.id,
        label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.userName,
      })));
    } catch (_) { }
  }, []);

  useEffect(() => {
    store.setmodule("nonlitigation_tasks");
    fetchOverdueTasks();
    fetchUsers();
  }, []);

  // ── client-side filtering ────────────────────────────────────────────────
  useEffect(() => {
    let filtered = allData.filter((task) => {
      const taskName = (task.subtask_title || "").toLowerCase();
      const projName = (task.project_name || "").toLowerCase();
      const assignee = (task.assigned_to_name || task.assigned_to_username || "").toLowerCase();
      const q = search.toLowerCase();

      const matchSearch = !q || taskName.includes(q) || projName.includes(q) || assignee.includes(q);
      const matchProject = projectFilter === "All Projects" || task.project_name === projectFilter;
      const matchUser = userFilter === "All Users" ||
        (task.assigned_to_name || task.assigned_to_username) === userFilter;
      const matchPriority = priorityFilter === "All Priorities" ||
        (task.priority || "").toUpperCase() === priorityFilter;

      let matchDays = true;
      if (daysFilter === "> 5 Days") matchDays = task.days_overdue > 5;
      if (daysFilter === "1-5 Days") matchDays = task.days_overdue >= 1 && task.days_overdue <= 5;

      return matchSearch && matchProject && matchUser && matchPriority && matchDays;
    });
    setFilteredData(filtered);
    setCurrentPage(1); // reset to first page on filter change
  }, [search, projectFilter, userFilter, priorityFilter, daysFilter, allData]);

  // ── client-side sorting ──────────────────────────────────────────────────
  const sortedData = useMemo(() => {
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      let aVal = a[sortField] ?? "";
      let bVal = b[sortField] ?? "";
      // numeric fields
      if (sortField === "days_overdue") {
        aVal = Number(aVal); bVal = Number(bVal);
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      // date fields
      if (["due_date", "assigned_to_date", "created_date", "work_start_date"].includes(sortField)) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      // string fields
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDir]);

  // ── pagination ───────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const pagedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort className="inline ml-1 opacity-30" size={10} />;
    return sortDir === "asc"
      ? <FaSortUp className="inline ml-1 text-[#4F46E5]" size={10} />
      : <FaSortDown className="inline ml-1 text-[#4F46E5]" size={10} />;
  };

  // ── notify handler ───────────────────────────────────────────────────────
  const handleNotify = async () => {
    if (!notifyModal) return;
    if (!notifyModal.assigned_to) {
      ErrorAlert("This task has no assigned user.");
      return;
    }
    setNotifying(true);
    try {
      const msg = reminderMessage || "";
      const res = await UpdateRecord(
        `/subtask/sendreminder/${notifyModal.id}/${user.id}/${notifyModal.assigned_to}?body=${encodeURIComponent(msg)}&note=${encodeURIComponent(msg)}&message=${encodeURIComponent(msg)}`
      );
      if (res.success) {
        SuccessAlert(`Reminder sent to ${notifyModal.assigned_to_name || "assigned user"}.`);
        setNotifyModal(null);
        setReminderMessage("");
      } else {
        ErrorAlert("Failed to send reminder.");
      }
    } catch (_) {
      ErrorAlert("Failed to send reminder.");
    } finally {
      setNotifying(false);
    }
  };

  // ── reassign handler ─────────────────────────────────────────────────────
  const handleReassign = async () => {
    if (!reassignModal || !selectedUser) {
      ErrorAlert("Please select a user to reassign.");
      return;
    }
    setReassigning(true);
    try {
      const res = await UpdateRecord(
        `/subtask/reassigntask/${reassignModal.id}/${selectedUser}/${user.id}`
      );
      if (res.success) {
        SuccessAlert("Task reassigned successfully.");
        setReassignModal(null);
        setSelectedUser("");
        fetchOverdueTasks();
      } else {
        ErrorAlert("Failed to reassign task.");
      }
    } catch (_) {
      ErrorAlert("Failed to reassign task.");
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div className="grid grid-rows-10 h-full w-full bg-[#f8fafc]">
      {/* Header */}
      <div className="row-span-1 flex justify-between items-center px-[30px] py-4 bg-white border-b border-slate-100 shadow-sm">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-800" style={{ fontFamily: 'Inter, sans-serif' }}>
            Overdue Tasks Master
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Monitor delayed subtasks, dispatch reminders, and adjust user assignments.
          </p>
        </div>
        <button
          onClick={fetchOverdueTasks}
          className="text-xs px-4 py-2 font-bold bg-white border rounded-xl hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
          style={{ borderColor: '#e2e8f0', color: '#4F46E5' }}
        >
          ↺ Refresh List
        </button>
      </div>

      <div className="row-span-9 h-full w-full overflow-y-auto">
        <div className="py-5 px-[30px] w-full">

      {/* Alert banner */}
      {!loading && filteredData.length > 0 && (
        <div className="px-4 py-3.5 rounded-2xl mb-6 flex items-center gap-2.5 shadow-sm" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FEE2E2', color: '#991B1B' }}>
          <FaBell className="text-[#EF4444] flex-shrink-0" />
          <span className="text-xs font-semibold">
            <strong className="font-extrabold">Overdue Alert — </strong>
            You have <strong className="font-extrabold">{filteredData.length}</strong> overdue task{filteredData.length !== 1 ? "s" : ""} requiring immediate review.
          </span>
        </div>
      )}

      {/* Filters (exactly like User screen search) */}
      <div className="p-5 rounded-2xl border border-slate-100 bg-[#fcfcfe] flex flex-col gap-4 shadow-sm mb-6">
        <div className="flex flex-col lg:flex-row gap-3.5 items-end">
          {/* Elegant Search Input */}
          <div className="relative flex-[2.5] w-full min-w-[280px] md:min-w-[380px]">
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="#6366F1" className="w-4.5 h-4.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.637 10.637z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by task, project, or user..."
              className="w-full pl-10 pr-10 py-2.5 bg-white border rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0' }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <FaTimes size={12} />
              </button>
            )}
          </div>

          {/* Project */}
          <div className="w-full lg:w-auto min-w-[150px]">
            <select
              className="w-full bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0' }}
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
            >
              <option>All Projects</option>
              {projectOpts.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          {/* Assigned User */}
          <div className="w-full lg:w-auto min-w-[150px]">
            <select
              className="w-full bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0' }}
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            >
              <option>All Users</option>
              {userOpts.map(u => <option key={u}>{u}</option>)}
            </select>
          </div>
          {/* Priority */}
          <div className="w-full lg:w-auto min-w-[150px]">
            <select
              className="w-full bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0' }}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option>All Priorities</option>
              <option>HIGH</option>
              <option>MEDIUM</option>
              <option>LOW</option>
            </select>
          </div>
          {/* Days Overdue */}
          <div className="w-full lg:w-auto min-w-[120px]">
            <select
              className="w-full bg-white border text-slate-700 text-xs font-semibold rounded-xl px-4 py-2.5 focus:outline-none transition-all duration-300"
              style={{ borderColor: '#e2e8f0' }}
              value={daysFilter}
              onChange={(e) => setDaysFilter(e.target.value)}
            >
              <option value="All">All Days</option>
              <option value="&gt; 5 Days">&gt; 5 Days</option>
              <option value="1-5 Days">1-5 Days</option>
            </select>
          </div>
        </div>

        {/* Dynamic Pills & Top Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mr-1">Status:</span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', border: '1px solid #E0E7FF' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#4F46E5] animate-pulse"></span>
              Tab Count ({filteredData.length})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold text-slate-500">Rows per page:</label>
            <select
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none bg-white"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table (Natural flow layout) */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-lg overflow-hidden flex flex-col mb-8" style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)' }}>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 font-semibold text-sm">Loading Overdue Items…</div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
            <FaExclamationTriangle size={28} className="text-[#F59E0B]" />
            <p className="text-xs font-semibold">No overdue tasks match the filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse" style={{ minWidth: '1100px' }}>
              <thead>
                {/* Light high-visibility headers */}
                <tr style={{ backgroundColor: '#EEF2FF', borderBottom: '2px solid #E2E8F0' }}>
                  <th className="px-4 py-4 text-[10px] font-black uppercase tracking-widest text-slate-600 w-12 text-center">
                    S.No.
                  </th>
                  {[
                    { label: "Task Name", field: "subtask_title" },
                    { label: "Project", field: "project_name" },
                    { label: "Document", field: "task_title" },
                    { label: "Assigned To", field: "assigned_to_name" },
                    { label: "Assigned Date", field: "assigned_to_date" },
                    { label: "Started Date", field: "work_start_date" },
                    { label: "Priority", field: "priority" },
                    { label: "Due Date", field: "due_date" },
                    { label: "Days Overdue", field: "days_overdue" },
                  ].map(({ label, field }) => (
                    <th
                       key={field}
                      className="px-4 py-4 text-[10px] font-black uppercase tracking-widest cursor-pointer select-none text-slate-600 hover:text-[#4F46E5]"
                      onClick={() => handleSort(field)}
                    >
                      {label}<SortIcon field={field} />
                    </th>
                  ))}
                  <th className="px-4 py-4 text-[10px] font-black uppercase tracking-widest text-center text-slate-600 pr-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pagedData.map((task, idx) => (
                  <tr
                    key={task.id || idx}
                    className="bg-white hover:bg-slate-50 transition-all duration-150 cursor-pointer"
                    onClick={() =>
                      navigate(`/${URLPRIFIX}/tasks/view/${task.id}`)
                    }
                  >
                    <td className="px-4 py-3.5 text-xs text-slate-500 font-semibold text-center w-12">
                      {(currentPage - 1) * pageSize + idx + 1}
                    </td>
                    <td className="px-4 py-3.5 font-bold text-slate-800 max-w-[200px] truncate text-xs">
                      {task.subtask_title || "—"}
                    </td>
                    <td className="px-4 py-3.5 text-xs font-semibold text-slate-700">{task.project_name || "—"}</td>
                    <td className="px-4 py-3.5 text-xs text-blue-600 font-semibold hover:underline">
                      {task.task_title ? `📄 ${task.task_title}` : "—"}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-600">
                      {task.assigned_to_name || task.assigned_to_username || "Unassigned"}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500">{formatDate(task.assigned_to_date || task.created_date)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-500">{formatDate(task.work_start_date)}</td>
                    <td className="px-4 py-3.5 text-xs">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${priorityClass(task.priority)}`}>
                        {(task.priority || "—").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-500">{formatDate(task.due_date)}</td>
                    <td className="px-4 py-3.5 text-xs">
                      <span className="bg-red-50 text-red-600 border border-red-200 px-2.5 py-0.5 rounded text-[10px] font-bold">
                        {task.days_overdue} day{task.days_overdue !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3.5 pr-6"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex gap-3 justify-center">
                        <button
                          id={`notify-nl-${task.id}`}
                          onClick={() => setNotifyModal(task)}
                          className="text-slate-400 hover:text-[#4F46E5] flex items-center gap-1 text-[11px] font-bold p-1 hover:bg-indigo-50 rounded cursor-pointer border-0 bg-transparent"
                          title="Send reminder"
                        >
                          <FaBell size={10} /> Notify
                        </button>
                        <button
                          id={`reassign-nl-${task.id}`}
                          onClick={() => { setReassignModal(task); setSelectedUser(""); }}
                          className="text-slate-400 hover:text-[#4F46E5] flex items-center gap-1 text-[11px] font-bold p-1 hover:bg-indigo-50 rounded cursor-pointer border-0 bg-transparent"
                          title="Reassign task"
                        >
                          <FaUserEdit size={11} /> Reassign
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && sortedData.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3.5 border-t border-slate-100 bg-[#fcfcfe]">
            <span className="text-[11px] font-bold text-slate-500">
              Showing {Math.min((currentPage - 1) * pageSize + 1, sortedData.length)}–{Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} tasks
            </span>
            <div className="flex items-center gap-1">
              <button
                className="px-2.5 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >«</button>
              <button
                className="px-2.5 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-2.5 py-1 rounded border text-xs font-bold ${currentPage === p
                        ? "text-white border-[#4F46E5]"
                        : "border-gray-300 hover:bg-gray-100"
                        }`}
                      style={{ backgroundColor: currentPage === p ? '#4F46E5' : '#FFFFFF' }}
                    >{p}</button>
                  )
                )}
              <button
                className="px-2.5 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >Next ›</button>
              <button
                className="px-2.5 py-1 rounded border border-gray-300 text-xs hover:bg-gray-100 disabled:opacity-40"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >»</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Notify Modal ───────────────────────────────────────────────────── */}
      {notifyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div
            className="rounded-xl shadow-2xl w-full relative"
            style={{
              maxWidth: "500px",
              backgroundColor: "#FFFFFF",
              padding: "24px",
            }}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={() => { setNotifyModal(null); setReminderMessage(""); }}
            >
              <FaTimes size={18} />
            </button>

            <h2
              className="text-xl font-bold mb-6 text-center"
              style={{ color: "#111827" }}
            >
              Send Reminder
            </h2>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Task Name
                </label>
                <input
                  type="text"
                  value={notifyModal.subtask_title}
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Assigned User
                </label>
                <input
                  type="text"
                  value={
                    notifyModal.assigned_to_name ||
                    notifyModal.assigned_to_username ||
                    "Unassigned"
                  }
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Message Content
                </label>
                <textarea
                  value={reminderMessage}
                  onChange={(e) => setReminderMessage(e.target.value)}
                  placeholder="Enter reminder message..."
                  className="w-full p-2.5 rounded-xl border text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  rows={3}
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#FFFFFF",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setNotifyModal(null); setReminderMessage(""); }}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200"
                  style={{
                    backgroundColor: "#E5E7EB",
                    color: "#374151",
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleNotify}
                  disabled={notifying}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                  }}
                >
                  {notifying ? "Sending..." : "Send Reminder"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign Modal ─────────────────────────────────────────────────── */}
      {reassignModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div
            className="rounded-xl shadow-2xl w-full relative"
            style={{
              maxWidth: "500px",
              backgroundColor: "#FFFFFF",
              padding: "24px",
            }}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={() => {
                setReassignModal(null);
                setSelectedUser("");
              }}
            >
              <FaTimes size={18} />
            </button>

            <h2
              className="text-xl font-bold mb-6 text-center"
              style={{ color: "#111827" }}
            >
              Reassign Task
            </h2>

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Task Name
                </label>
                <input
                  type="text"
                  value={reassignModal.subtask_title}
                  readOnly
                  className="w-full p-2 rounded-md border"
                  style={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    color: "#1F2937",
                  }}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "#374151" }}
                >
                  Select New Assignee
                </label>

                <Select
                  options={allUsers}
                  value={allUsers.find((u) => u.value === selectedUser) || null}
                  onChange={(selected) =>
                    setSelectedUser(selected ? selected.value : "")
                  }
                  placeholder="Search and select user..."
                  isSearchable
                  isClearable
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: "42px",
                      borderColor: "#D1D5DB",
                      backgroundColor: "#F9FAFB",
                      borderRadius: "6px",
                      boxShadow: "none",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? "#EFF6FF" : "#FFFFFF",
                      color: "#1F2937",
                      cursor: "pointer",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "#6B7280",
                    }),
                  }}
                />
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setReassignModal(null);
                    setSelectedUser("");
                  }}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200"
                  style={{
                    backgroundColor: "#E5E7EB",
                    color: "#374151",
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={handleReassign}
                  disabled={reassigning || !selectedUser}
                  className="px-5 py-2 rounded-md font-semibold transition-colors duration-200 disabled:opacity-50"
                  style={{
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                  }}
                >
                  {reassigning ? "Reassigning..." : "Reassign"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default OverdueTasks;
